/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import config from "../../../../share/config";
import errors from "../../../../common/nodejs/errors";
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: "standby",
  START: "start",
  SUCCESS: "success",
  CANCEL: "cancel",
  FAILED: "failed",
  ERROR: "error",
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standby',
  EXECUTE: 'execute',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

/** DefaultSession遷移の再送回数最大 */
const TRANSITION_RETRY_MAX_COUNT = 3;

export default {
  name: "requirement075",
  data() {
    return {
      phase: PHASE.STANDBY,
      nextPhase: "",
      isLoading: false,
      isCompletedNonDefaultSession: false, //NonDefaultSession遷移完了フラグ
      retryCount: null, //再送回数
      stopChangePhase: false, // 画面遷移をしないフラグ
      modal_requirement_075: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.START:
        case PHASE.CANCEL:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_075 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_075 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 画面遷移をしないフラグのチェック
      if (!this.stopChangePhase) {
        // 異常終了：エラー表示
        this.phase = PHASE.ERROR;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 
     * 機能終了
     */
    async exit() {
      // 画面遷移をしないフラグをON
      this.stopChangePhase = true;
      // Dispose用APIを実行後に画面を閉じる
      await this.doDispose();
      // 画面を閉じる
      this.closeWizard();
      // 初期設定に戻す
      this.phase = PHASE.START;
      this.isCompletedNonDefaultSession = false;
      this.nextPhase = "";
      this.retryCount = null;
      this.stopChangePhase = false;
    },

    /** 画面起動時の起動処理 */
    async doStandby() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.STANDBY
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 成功
              this.isCompletedNonDefaultSession = true;
              this.phase = PHASE.START;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              switch (response.data.async_code) {
                case errors.WSP_075_SECURITY_ERROR:
                  // セキュリティアクセスでエラー
                  this.isCompletedNonDefaultSession = true;
                  break;
                default:
                  // 何もしない
                  break;
              }
              // 異常終了
              // 通信エラーへ遷移
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * 光軸調整準備実施 
     */
    async doExecute() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.EXECUTE
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.nextPhase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (response.data.async_code) {
                case errors.WSP_075_ERROR_FINISH:
                  // 0x0000 にて終了
                  this.nextPhase = PHASE.FAILED;
                  break;
                default:
                  // 上記以外のエラー
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              // 通信エラー
              this.showUnexpectedError();
              break;
          }
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * DefaultSession遷移
     */
    async doTerminate() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE
        };

        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.isCompletedNonDefaultSession = false;
              break;
            case config.STATUS_WSP_FAILED:
              // DefaultSession遷移にてエラー
              this.retryCount = response.data.user_data ? response.data.user_data.retryCount : undefined;
              this.showUnexpectedError();
              break;
            default:
              // 異常終了
              this.showUnexpectedError();
              break;
          }
        }

      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 本ダイアログを閉じるときの処理(Dispose APIの実行)
     */
    async doDispose() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.DISPOSE
        };

        // 初期処理（要求）
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** 
     * 通信エラー画面でのDefaultSession遷移要否判定 
     * @returns {boolean} 
     */
    isTerminateNeeded() {
      // 以下のいずれかの場合実施しない
      // 現在セッションがデフォルトセッションの場合
      if (!this.isCompletedNonDefaultSession) {
        return false;
      }

      // ECU独自仕様の「再送」を実行済みの場合
      if (this.retryCount === TRANSITION_RETRY_MAX_COUNT) {
        return false;
      }

      return true;
    },

    // ユーザ操作-------------------------------------------

    /** 初期画面からOK押下時の処理 */
    async onStartOkClicked() {
      // 光軸調整準備実施
      await this.doExecute();

      // DefaultSession遷移
      // 正常終了または異常終了(0x0000)の時
      if (this.nextPhase === PHASE.SUCCESS || this.nextPhase === PHASE.FAILED) {
        await this.doTerminate();
        // DefaultSession遷移エラーの時は正常終了画面/異常終了画面に遷移しない
        if (this.phase !== PHASE.ERROR) {
          this.phase = this.nextPhase;
        }
        this.nextPhase = "";
      }
    },

    /** 初期画面からキャンセル押下時の処理 */
    onStartCancelClicked() {
      // キャンセル表示画面へ遷移
      this.phase = PHASE.CANCEL;
    },

    /** キャンセル表示画面でYES押下時の処理 */
    async onCancelYesClicked() {
      // DefaultSession遷移
      await this.doTerminate();

      // 異常終了でない場合、終了処理
      if (this.phase !== PHASE.ERROR) {
        this.exit();
      }
    },

    /** キャンセル表示画面でNO押下時の処理 */
    onCancelNoClicked() {
      // 初期画面へ遷移
      this.phase = PHASE.START;
    },

    /** 正常終了画面でOK押下時の処理 */
    onSuccessOkClicked() {
      // 機能終了
      // DefaultSession遷移は実施済み
      this.exit();
    },

    /** 異常終了画面でOK押下時の処理 */
    onFailedOkClicked() {
      // 機能終了
      // DefaultSession遷移は実施済み
      this.exit();
    },

    /** 通信エラー画面でOK押下時の処理 */
    async onErrorOkClicked() {
      try {
        // DefaultSession遷移
        // 条件を満たす場合のみ実施
        if (this.isTerminateNeeded()) {
          await this.doTerminate();
        }
      } finally {
        // 終了処理
        // DefaultSession遷移の結果にかかわらず機能終了
        this.exit();
      }
    }
  },

  async mounted() {
    this.init();
    this.doStandby();
  }
};
